import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { CreateTangramService } from '../create-tangram/crearte-tangram.service';

@Component({
  selector: 'tangram-bulk-tangram-creation',
  templateUrl: './bulk-tangram-creation.component.html',
  styleUrls: ['./bulk-tangram-creation.component.scss']
})
export class BulkTangramCreationComponent {
  selectedSyllabus = '';
  selectedSyllabusFile: File | null = null;
  links: string[] = [];
  isGenerating = false;
  isCreating = false;
  generatedItems: Array<{ title: string; editable: boolean; link: string }> = [];
  errorMessage = '';
  createMessage = '';
  createdPathwayId = '';

  constructor(
    private createTangramService: CreateTangramService,
    private toasterService: ToastrService,
    private router: Router
  ) {}

  onFileSelected(payload: { fileName: string; file: File | null }) {
    this.selectedSyllabus = (payload && payload.fileName) || '';
    this.selectedSyllabusFile = (payload && payload.file) || null;
  }

  onLinksChanged(links: string[]) {
    this.links = links || [];
  }

  async generatePreview() {
    this.errorMessage = '';
    this.createMessage = '';
    this.createdPathwayId = '';
    if (!this.selectedSyllabus && this.links.length === 0) {
      this.errorMessage = 'Add a syllabus file or at least one link to generate preview.';
      this.generatedItems = [];
      return;
    }
    this.isGenerating = true;
    const extractedFromSyllabus = await this.extractLinksFromSyllabusFile();
    const allLinks = this.deduplicateLinks([...(this.links || []), ...extractedFromSyllabus]);
    if (!allLinks.length) {
      this.isGenerating = false;
      this.errorMessage = 'No valid links found. Add links manually or upload a text syllabus containing URLs.';
      this.generatedItems = [];
      return;
    }
    this.generatedItems = allLinks.map((link: string, index: number) => ({
      title: this.deriveTitleFromLink(link, index),
      editable: true,
      link: link
    }));
    this.isGenerating = false;
  }

  async createTangram() {
    this.createMessage = '';
    if (this.generatedItems.length === 0) {
      this.errorMessage = 'Generate preview before creating a Tangram.';
      return;
    }
    this.errorMessage = '';
    this.isCreating = true;
    let pathwayId = '';
    const failedLinks: string[] = [];

    for (let index = 0; index < this.generatedItems.length; index++) {
      const item = this.generatedItems[index];
      const payload = this.buildVideoPayload(item, pathwayId);
      try {
        const response: any = await this.resolveObservable(this.createTangramService.saveVideo(payload));
        pathwayId = (response && response.pathwayId) || pathwayId;
      } catch (error) {
        failedLinks.push(item.link);
      }
    }

    this.isCreating = false;
    this.createdPathwayId = pathwayId;
    if (!pathwayId) {
      this.errorMessage = 'Unable to create Tangram. Please verify links and try again.';
      return;
    }

    if (failedLinks.length > 0) {
      this.createMessage = `Tangram created with partial success. ${failedLinks.length} link(s) failed.`;
      this.toasterService.warning(this.createMessage);
    } else {
      this.createMessage = 'Tangram created successfully from bulk input.';
      this.toasterService.success(this.createMessage);
    }

    this.router.navigate(['/share-tangram'], { queryParams: { pathwayId: pathwayId } });
  }

  private async extractLinksFromSyllabusFile(): Promise<string[]> {
    if (!this.selectedSyllabusFile) {
      return [];
    }
    const fileType = this.selectedSyllabusFile.type || '';
    const fileName = this.selectedSyllabusFile.name || '';
    const canReadAsText = fileType.indexOf('text/') === 0 ||
      fileName.endsWith('.txt') ||
      fileName.endsWith('.md') ||
      fileName.endsWith('.csv');
    if (!canReadAsText) {
      return [];
    }
    try {
      const content = await this.readFileAsText(this.selectedSyllabusFile);
      const matches = content.match(/https?:\/\/[^\s)]+/g) || [];
      return matches;
    } catch (error) {
      return [];
    }
  }

  private deduplicateLinks(links: string[]): string[] {
    const seen: { [key: string]: boolean } = {};
    return (links || [])
      .map((link) => (link || '').trim())
      .filter((link) => !!link)
      .filter((link) => {
        if (seen[link]) {
          return false;
        }
        seen[link] = true;
        return true;
      });
  }

  private deriveTitleFromLink(link: string, index: number): string {
    const cleanLink = (link || '').replace(/^https?:\/\//, '');
    return `Module ${index + 1}: ${cleanLink.slice(0, 48)}`;
  }

  private detectPlatform(link: string): string {
    const normalized = (link || '').toLowerCase();
    if (normalized.indexOf('youtube.com') > -1 || normalized.indexOf('youtu.be') > -1) {
      return 'YouTube';
    }
    if (normalized.indexOf('spotify.com') > -1) {
      return 'Spotify';
    }
    return 'Other';
  }

  private buildVideoPayload(item: { title: string; editable: boolean; link: string }, pathwayId: string): any {
    const defaultDuration = { hour: 0, minute: 0, second: 0 };
    const defaultTime = { hour: 0, minute: 0, second: 0 };
    const platform = this.detectPlatform(item.link);
    const thumbnail = this.resolveThumbnail(item.link, platform);
    return {
      _id: null,
      pathwayId: pathwayId || '',
      videoType: '',
      youtubeVideoId: item.link,
      title: item.title,
      order: '',
      description: 'Created from Bulk Tangram Creation.',
      thumbnail: thumbnail,
      duration: defaultDuration,
      tagId: '',
      startTime: defaultTime,
      endTime: defaultTime,
      startTimeInSecond: 0,
      endTimeInSecond: 0,
      platForm: platform
    };
  }

  private resolveThumbnail(link: string, platform: string): { smallURL: string; mediumURL: string } {
    const normalizedLink = (link || '').toLowerCase();
    const isImage = /\.(png|jpe?g|gif|bmp|ico|svg)(\?.*)?$/.test(normalizedLink);
    if (isImage) {
      return { smallURL: link, mediumURL: link };
    }

    if (platform === 'YouTube') {
      const youtubeId = this.extractYoutubeId(link);
      if (youtubeId) {
        const youtubeThumb = `https://img.youtube.com/vi/${youtubeId}/mqdefault.jpg`;
        return { smallURL: youtubeThumb, mediumURL: youtubeThumb };
      }
    }

    if (platform === 'Spotify') {
      const audioPlaceholder = 'assets/img/videos/audio-placeholder.jpg';
      return { smallURL: audioPlaceholder, mediumURL: audioPlaceholder };
    }

    if (normalizedLink.indexOf('.pdf') > -1) {
      const pdfPlaceholder = 'assets/img/videos/PDF_Placeholder.png';
      return { smallURL: pdfPlaceholder, mediumURL: pdfPlaceholder };
    }

    if (normalizedLink.indexOf('drive.google.com') > -1 || normalizedLink.indexOf('docs.google.com') > -1) {
      const drivePlaceholder = 'assets/img/videos/Drive_Placeholder.png';
      return { smallURL: drivePlaceholder, mediumURL: drivePlaceholder };
    }

    const articlePlaceholder = 'assets/img/videos/Blog_Articles_Placeholder.png';
    return { smallURL: articlePlaceholder, mediumURL: articlePlaceholder };
  }

  private extractYoutubeId(link: string): string {
    try {
      if (link.indexOf('youtu.be') > -1) {
        const url = new URL(link);
        return (url.pathname || '').replace('/', '');
      }
      if (link.indexOf('youtube.com') > -1) {
        const url = new URL(link);
        return url.searchParams.get('v') || '';
      }
    } catch (error) {
      return '';
    }
    return '';
  }

  private resolveObservable(observable: Observable<any>): Promise<any> {
    return new Promise((resolve, reject) => {
      observable.subscribe(
        (response) => resolve(response),
        (error) => reject(error)
      );
    });
  }

  private readFileAsText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve((reader.result as string) || '');
      reader.onerror = () => reject(reader.error);
      reader.readAsText(file);
    });
  }
}
